import React, { MouseEventHandler, useCallback, useMemo, useState } from 'react';
import { Box, Chip, Spinner, Text, config } from 'folds';
import { Room } from 'matrix-js-sdk';
import { useTranslation } from 'react-i18next';

import * as css from './RoomSidePanel.css';
import { ThreadMenuItem } from './ThreadMenuItem';
import { sortThreadsForPanel } from './sortThreadsForPanel';
import { useRoomThreads } from '../../hooks/useRoomThreads';
import { useMediaAuthentication } from '../../hooks/useMediaAuthentication';
import { useThreadChat } from '../../state/threadChat';
import { useStarredThreadsByRoom } from '../../hooks/useStarredThreads';

type ThreadsPanelProps = {
  room: Room;
};

const THREAD_PREVIEW_THRESHOLD = 10;

export function ThreadsPanel({ room }: ThreadsPanelProps) {
  const { t } = useTranslation();
  const useAuthentication = useMediaAuthentication();
  const [threadChat, setThreadChat] = useThreadChat(room.roomId);
  const [showAllThreads, setShowAllThreads] = useState(false);
  const { threads: starredThreads, threadIds: starredThreadIds } = useStarredThreadsByRoom(
    room.roomId,
  );

  const { threads, loading, error, retry } = useRoomThreads(room);

  const sortedThreads = useMemo(
    () => sortThreadsForPanel(threads, starredThreads),
    [starredThreads, threads],
  );

  const shouldShowThreadsPreview =
    !showAllThreads && sortedThreads.length > THREAD_PREVIEW_THRESHOLD;
  const displayThreads = shouldShowThreadsPreview
    ? sortedThreads.slice(0, THREAD_PREVIEW_THRESHOLD - 1)
    : sortedThreads;

  const handleThreadClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (evt) => {
      const rootId = evt.currentTarget.getAttribute('data-event-id');
      if (!rootId) return;

      setThreadChat({ open: true, threadRootId: rootId });
    },
    [setThreadChat],
  );

  if (!loading && !error && sortedThreads.length === 0) return null;

  return (
    <Box direction="Column" gap="100">
      <Text className={css.MembersGroupLabel} size="L400" priority="300">
        {t('room.threads')}
      </Text>

      {loading && (
        <Box justifyContent="Center" style={{ padding: config.space.S200 }}>
          <Spinner />
        </Box>
      )}

      {!loading && error && (
        <Box
          direction="Column"
          alignItems="Center"
          gap="100"
          style={{ padding: config.space.S300 }}
        >
          <Text align="Center" size="T300" priority="300">
            {t('room.threadsLoadFailed')}
          </Text>
          <Chip as="button" variant="SurfaceVariant" size="400" radii="300" onClick={retry}>
            <Text size="T200">{t('common.retry')}</Text>
          </Chip>
        </Box>
      )}

      {!loading && !error && sortedThreads.length > 0 && (
        <>
          <Box direction="Column" gap="200">
            {displayThreads.map((thread) => (
              <ThreadMenuItem
                key={thread.id}
                useAuthentication={useAuthentication}
                room={room}
                thread={thread}
                isStarred={starredThreadIds.has(thread.id)}
                selected={threadChat.open && threadChat.threadRootId === thread.id}
                onClick={handleThreadClick}
              />
            ))}
          </Box>

          {shouldShowThreadsPreview && (
            <Box justifyContent="Center">
              <Chip
                as="button"
                variant="SurfaceVariant"
                fill="None"
                size="500"
                radii="300"
                onClick={() => setShowAllThreads(true)}
              >
                <Text size="T200">{`${t('room.viewAllThreads')} (${sortedThreads.length})`}</Text>
              </Chip>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
